import React from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ExternalLink, LayoutDashboard, Key, BarChart2, Activity, Network, Headset, Contact } from 'lucide-react';

const Portals = () => {
  return (
    <Layout>
      <PageHeader 
        title="EverestCloud" 
        highlight="Portals"
        description="A unified digital experience for managing your sovereign cloud resources, security, and billing. Access everything you need from a single pane of glass."
      />

      <section className="py-24 bg-white dark:bg-slate-950 transition-colors duration-500">
        <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

                <PortalCard 
                    icon={<Headset className="h-10 w-10 text-accent"/>}
                    title="Service Desk"
                    description="Access the EverestCloud Service Desk to submit and track support tickets, manage incidents, and get help from our team."
                    linkText="Open Service Desk"
                    href="/support/login"
                />

                <PortalCard 
                    icon={<Contact className="h-10 w-10 text-accent"/>}
                    title="CRM"
                    description="Manage customer relationships, track deals, and access sales analytics through the EverestCloud CRM portal."
                    linkText="Open CRM"
                    href="/crm/login"
                />
                
                <PortalCard 
                    icon={<LayoutDashboard className="h-10 w-10 text-accent"/>}
                    title="EverestCloud Console"
                    description="The central command center for your tenant. Provision VMs, manage storage, and configure networks."
                    linkText="Login to Console"
                />

                <PortalCard 
                    icon={<Key className="h-10 w-10 text-accent"/>}
                    title="EverestCloud Vault"
                    description="Secure access management. Handle PAM (Privileged Access Management) and MFA settings."
                    linkText="Access Vault"
                />

                <PortalCard 
                    icon={<BarChart2 className="h-10 w-10 text-accent"/>}
                    title="EverestCloud Insight"
                    description="Deep observability. View resource utilization trends, cost analysis, and optimization recommendations."
                    linkText="View Insights"
                />

                 <PortalCard 
                    icon={<Activity className="h-10 w-10 text-accent"/>}
                    title="EverestCloud Pulse"
                    description="Real-time status dashboard. Monitor the health and availability of all EverestCloud services."
                    linkText="Check Status"
                />

                 <PortalCard 
                    icon={<Network className="h-10 w-10 text-accent"/>}
                    title="EverestCloud Atlas"
                    description="IPAM (IP Address Management) and DCIM (Data Center Infrastructure Management) tools."
                    linkText="Launch Atlas"
                />

            </div>
        </div>
      </section>
    </Layout>
  );
};

const PortalCard = ({ icon, title, description, linkText, href }) => {
    const buttonContent = (
        <Button variant="outline" className="w-full h-12 gap-2 group border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:border-accent/30 hover:bg-accent/5" data-testid={`portal-card-btn-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            {linkText} <ExternalLink className="h-4 w-4 group-hover:text-accent transition-colors" />
        </Button>
    );

    return (
        <Card className="flex flex-col h-full bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-white/5 hover:border-accent/40 transition-colors cursor-default hover:bg-white dark:hover:bg-slate-900/80 backdrop-blur-sm group shadow-md dark:shadow-none hover:shadow-xl dark:hover:shadow-[0_0_30px_rgba(234,179,8,0.1)]" data-testid={`portal-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardHeader>
                <div className="mb-6 p-4 rounded-xl bg-white dark:bg-slate-900 w-fit group-hover:scale-110 transition-transform shadow-sm">{icon}</div>
                <CardTitle className="text-slate-900 dark:text-white text-2xl">{title}</CardTitle>
                <CardDescription className="text-slate-600 dark:text-slate-400 text-base mt-2 leading-relaxed">{description}</CardDescription>
            </CardHeader>
            <CardContent className="flex-grow">
                {/* Spacer */}
            </CardContent>
            <CardFooter>
                {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="w-full" data-testid={`portal-link-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                        {buttonContent}
                    </a>
                ) : (
                    buttonContent
                )}
            </CardFooter>
        </Card>
    );
};

export default Portals;
